import { Injectable, inject, signal } from '@angular/core';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HdGateInfoOpenBehavior } from '../bodygraph/gate-info/hd-gate-info-requested-context.model';
import { loadHdChartGlobalSettings } from '../bodygraph/chart-tabs/hd-chart-global-settings';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdEmotionalWavesPanelComponent } from './hd-emotional-waves-panel.component';
import {
  HD_EMOTIONAL_WAVES_PANEL_KEY,
  HdEmotionalWaveRef,
  HdEmotionalWavesPanelData,
  hdEmotionalWaveForGate,
} from './hd-emotional-waves.metadata';

/**
 * Opent het golvenpaneel (overzicht van de emotionele golven).
 *
 * Zelfde patroon als `HdVoicesPanelService`: de dock-/dialoogkeuze komt uit de
 * globale `gateInfoOpenBehavior`-setting die de chart-pagina's live doorgeven
 * via {@link setOpenBehavior}. Leestekst → altijd de `'full'`-dockzone. Eén
 * paneel (key `emotional-waves`); opnieuw openen voor een andere poort
 * hergebruikt het paneel en scrollt naar de golf van die poort.
 */
@Injectable({ providedIn: 'root' })
export class HdEmotionalWavesPanelService {
  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly openBehavior = signal<HdGateInfoOpenBehavior>(
    loadHdChartGlobalSettings().gateInfoOpenBehavior,
  );

  /** Live-sync van de `gateInfoOpenBehavior`-setting door de chart-pagina's. */
  setOpenBehavior(behavior: HdGateInfoOpenBehavior): void {
    this.openBehavior.set(behavior);
  }

  /**
   * Opent het paneel gescrolld naar de golf van deze poort (poort 6 → de bron).
   * Geen emotionele poort → no-op.
   */
  openForGate(
    gate: number,
    origin?: { x: number; y: number },
    definedChannelKeys?: readonly string[],
  ): void {
    const focusWave = hdEmotionalWaveForGate(gate);
    if (focusWave == null) {
      return;
    }
    this.openForWave(focusWave, origin, definedChannelKeys);
  }

  /** Opent het paneel gescrolld naar deze golf (of de bron), bv. vanaf een kanaal. */
  openForWave(
    focusWave: HdEmotionalWaveRef,
    origin?: { x: number; y: number },
    definedChannelKeys?: readonly string[],
  ): void {
    this.open({ focusWave, definedChannelKeys }, origin);
  }

  private open(data: HdEmotionalWavesPanelData, origin?: { x: number; y: number }): void {
    const behavior = this.openBehavior();
    const docked = behavior === 'full-docked-keep' || behavior === 'full-docked-autoclose';

    const ref = this.floatingPanels.openOrFocus<HdEmotionalWavesPanelComponent, HdEmotionalWavesPanelData>({
      key: HD_EMOTIONAL_WAVES_PANEL_KEY,
      component: HdEmotionalWavesPanelComponent,
      data,
      dockTab: { label: this.dockTabLabel(), icon: 'wave-sine' },
      dockZone: 'full',
      openInitiallyDocked: docked,
      redockOnReopen: docked,
      startPosition: origin != null
        ? { preferred: origin, hint: 'prefer-right-of-anchor' }
        : 'center',
    });
    // Ook een al open paneel de nieuwe focus-golf aanreiken (verse referentie
    // zodat de auto-scroll opnieuw wapent, ook bij dezelfde golf).
    ref.replaceData({ ...data });
  }

  /** Kort dock-label ("Golven") uit de emotional-waves-scope, met fallback. */
  private dockTabLabel(): string {
    const lang = this.contentLang.effectiveLang();
    const label =
      this.gateInfo.cheatSheetTextIn('emotionalWaves', 'panel.dockLabel', lang) ||
      this.gateInfo.cheatSheetTextIn('emotionalWaves', 'panel.dockLabel', 'nl');
    return label || 'Waves';
  }
}
